import { ArrowLeft, Settings } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'react-hot-toast';
import { NavLink, useParams, useSearchParams } from 'react-router';
import {
  useGetProductLengthsByProductId,
  useUpsertSellerInventoryAndProductConfiguration,
  useUpsertSellerProductConfiguration,
} from '~/api/my-products';
import { ProductSettingModal } from '~/components/cards/ProductSettingModal';
import { Modal } from '~/components/overlays';
import { Button, Form, RadioGroup, useFormData } from '~/components/ui';
import { RetryBanner } from '~/components/ui/RetryBanner';
import { SellerProductConfigurationScope, UnitCode } from '~/graphql/graphql';
import type {
  GetProductLengthsByProductIdQuery,
  SellerProductStatus,
  Unit,
  UpsertSellerInventoryAndProductConfigurationMutationVariables,
} from '~/graphql/graphql';
import type { FormField } from '~/types';
import {
  buildScopeArgsFromConfiguration,
  type ClientError,
  extractGraphQLErrorMessages,
  isNotFoundGraphQLError,
  isUuid,
  parseNonNegativeInt,
} from '~/utils';
import { OrganizationScopeConfirmDialog } from './OrganizationScopeConfirmDialog';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { getPropertyStringSafe } from '~/utils/properties.tsx';
import { isUnitAllowedForCategory } from '~/utils/productUnitPolicy';

// The editor query selects a subset of SellerProductInventory; type helpers to
// that actual shape rather than the full generated type, so field access is
// checked against what the query really fetches (no widening `as` casts).
type EditorSellerProductInventory = NonNullable<
  GetProductLengthsByProductIdQuery['sellerProductInventory']
>;
import { useSelector } from 'react-redux';
import type { RootState } from '~/redux/store';

export function EditMyProduct() {
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showOrgScopeConfirm, setShowOrgScopeConfirm] = useState(false);
  const [selectedScope, setSelectedScope] =
    useState<SellerProductConfigurationScope>(
      SellerProductConfigurationScope.Facility
    );
  const [scopeSyncedForConfigId, setScopeSyncedForConfigId] = useState<
    string | null
  >(null);
  const { t } = useTranslation('my-product');

  const inventoryId = useParams()?.inventoryId || '';
  const [searchParams] = useSearchParams();
  const rawReturnPath = searchParams.get('returnPath');
  const returnPath =
    rawReturnPath?.startsWith('/') && !rawReturnPath.startsWith('//')
      ? rawReturnPath
      : '/my-products';
  const { data, isError, error, refetch } =
    useGetProductLengthsByProductId(inventoryId);
  const sellerProductInventory = data?.sellerProductInventory;
  const isProductMissing =
    !isUuid(inventoryId) ||
    (!sellerProductInventory &&
      (isError
        ? isNotFoundGraphQLError(error as ClientError)
        : data !== undefined));
  const isProductUnreadable =
    !sellerProductInventory && isError && !isProductMissing;
  const productCategory =
    sellerProductInventory?.platformProductsLength.platformProduct.category;
  const rawInventoryUnitCode = (sellerProductInventory
    ?.sellerProductConfiguration?.unit ??
    sellerProductInventory?.quantity?.unit) as UnitCode;
  const inventoryUnitCode = isUnitAllowedForCategory(
    productCategory,
    rawInventoryUnitCode
  )
    ? rawInventoryUnitCode
    : UnitCode.Pcs;

  const loadedScope =
    sellerProductInventory?.sellerProductConfiguration?.scope ??
    SellerProductConfigurationScope.Facility;
  const configurationId =
    sellerProductInventory?.sellerProductConfiguration?.id ?? null;

  // Initialize the toggle from the loaded configuration's scope once it loads
  // (and whenever the loaded configuration changes, including switching to a
  // product with no configuration, where loadedScope resets to FACILITY).
  // Adjusting state during render is React's recommended alternative to a
  // setState-in-effect sync.
  if (configurationId !== scopeSyncedForConfigId) {
    setScopeSyncedForConfigId(configurationId);
    setSelectedScope(loadedScope);
  }

  const handleScopeChange = (_name: string, value: string) => {
    setSelectedScope(value as SellerProductConfigurationScope);
  };

  const initialData = getInitialFormData(
    sellerProductInventory,
    inventoryUnitCode
  );

  const units = useSelector((state: RootState) => state.auth.units);

  const formData = useFormData({ initialData });

  const { mutate: upsertSellerProduct, isPending: isUpsertPending } =
    useUpsertSellerInventoryAndProductConfiguration();

  const {
    mutate: upsertSellerProductConfiguration,
    isPending: isUpsertConfigurationPending,
  } = useUpsertSellerProductConfiguration();

  const handleSaveChanges = () => {
    if (!sellerProductInventory) {
      toast.error(t('current_products.data_not_loaded'));
      return;
    }

    const readyToShipDays = parseNonNegativeInt(formData?.data.readyToShipDays);
    if (readyToShipDays === null) {
      toast.error(t('edit_product.errors.ready_to_ship_negative'));
      return;
    }

    // Switching a per-facility configuration to an organization-wide one is a
    // destructive change (it replaces every per-facility setting), so confirm
    // before the mutation fires.
    const switchingToOrganization =
      selectedScope === SellerProductConfigurationScope.Organization &&
      loadedScope !== SellerProductConfigurationScope.Organization;
    if (switchingToOrganization) {
      setShowOrgScopeConfirm(true);
      return;
    }

    submitSaveChanges();
  };

  const submitSaveChanges = () => {
    if (!sellerProductInventory) {
      toast.error(t('current_products.data_not_loaded'));
      return;
    }

    const readyToShipDays = parseNonNegativeInt(formData?.data.readyToShipDays);
    if (readyToShipDays === null) {
      toast.error(t('edit_product.errors.ready_to_ship_negative'));
      return;
    }

    const configurationInput = buildConfigurationInput(
      formData?.data,
      readyToShipDays,
      sellerProductInventory,
      selectedScope,
      loadedScope
    );

    const payload = buildMutationPayload(
      formData?.data,
      sellerProductInventory,
      selectedScope,
      configurationInput
    );

    upsertSellerProduct(payload, {
      onSuccess: () => {
        setShowOrgScopeConfirm(false);
        toast.success(t('messages.product_inventory_updated_successfully'));
      },
      onError: (error) =>
        toast.error(extractGraphQLErrorMessages(error).join(', ')),
    });
  };

  const handleSaveSettings = (
    criticalStockLevel: number,
    lowStockLevel: number
  ) => {
    if (!sellerProductInventory) {
      toast.error(t('current_products.data_not_loaded'));
      return;
    }

    const currentReplenishment =
      sellerProductInventory?.sellerProductConfiguration
        ?.replenishmentRatePerWeek;
    const settingsUnit = inventoryUnitCode;
    // Stock-monitoring settings never change scope — target the same-scoped
    // configuration derived from the current config, never locationId.

    const scopeArgs = sellerProductInventory.sellerProductConfiguration
      ? buildScopeArgsFromConfiguration(
          sellerProductInventory.sellerProductConfiguration
        )
      : {
          scope: SellerProductConfigurationScope.Facility,
          facilityId: sellerProductInventory.location.facility.id,
        };
    const payload = {
      platformProductLengthId:
        sellerProductInventory?.platformProductsLength.id,
      ...scopeArgs,
      sellerProductConfigurationInput: {
        criticalStockLevel: { value: criticalStockLevel, unit: settingsUnit },
        lowStockLevel: { value: lowStockLevel, unit: settingsUnit },
        replenishmentRatePerWeek: {
          value: currentReplenishment?.value ?? 0,
          unit: currentReplenishment?.unit || settingsUnit,
        },
      },
    };
    upsertSellerProductConfiguration(payload, {
      onSuccess: () => {
        toast.success(t('messages.product_updated_successfully'));
        setShowSettingsModal(false);
      },
      onError: (error) =>
        toast.error(extractGraphQLErrorMessages(error).join(', ')),
    });
  };

  return (
    <>
      <Modal
        isOpen={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
      >
        <ProductSettingModal
          initialCriticalStockLevel={
            sellerProductInventory?.sellerProductConfiguration
              ?.criticalStockLevel?.value || 0
          }
          initialLowStockLevel={
            sellerProductInventory?.sellerProductConfiguration?.lowStockLevel
              ?.value || 0
          }
          stockQuantity={sellerProductInventory?.quantity?.value || 0}
          replenishmentRate={
            sellerProductInventory?.sellerProductConfiguration
              ?.replenishmentRatePerWeek?.value || 0
          }
          onClose={() => setShowSettingsModal(false)}
          onSave={handleSaveSettings}
          isSaving={isUpsertConfigurationPending}
        />
      </Modal>
      <OrganizationScopeConfirmDialog
        isOpen={showOrgScopeConfirm}
        isSaving={isUpsertPending}
        onConfirm={submitSaveChanges}
        onCancel={() => setShowOrgScopeConfirm(false)}
      />
      <div className="bg-neut-0 min-h-screen">
        <div className="bg-neut-0 border-b border-neut-200">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center gap-4">
              <NavLink
                to={returnPath}
                className="p-2 hover:bg-neut-100 rounded-lg transition-colors"
                data-testid="back-to-my-products"
              >
                <ArrowLeft size={20} />
              </NavLink>
              <h1 className="text-xl text-neut-prim font-semibold">
                {t('edit_product.title')}
              </h1>
            </div>
            {!isProductMissing && !isProductUnreadable && (
              <Button
                onClick={handleSaveChanges}
                disabled={isUpsertPending || !sellerProductInventory}
                width="12rem"
              >
                {isUpsertPending
                  ? t('edit_product.saving')
                  : t('edit_product.save_changes')}
              </Button>
            )}
          </div>
        </div>
        {isProductMissing && (
          <div
            className="px-6 py-6 flex flex-col gap-2 text-sm"
            data-testid="edit-product-not-found"
          >
            <p className="text-neut-prim font-semibold">
              {t('edit_product.not_found.title')}
            </p>
            <NavLink to={returnPath} className="text-prim-600 hover:underline">
              {t('edit_product.not_found.back')}
            </NavLink>
          </div>
        )}
        {isProductUnreadable && (
          <RetryBanner
            className="px-6 py-6"
            message={t('edit_product.load_error.message')}
            retryLabel={t('edit_product.load_error.retry')}
            onRetry={() => void refetch()}
            testId="edit-product"
          />
        )}
        {!isProductMissing && !isProductUnreadable && (
          <div className="space-y-8 px-6 py-6">
            <div>
              <h2 className="text-lg text-neut-prim font-semibold mb-4">
                {t('edit_product.sections.product_information')}
              </h2>
              <div className="p-4 bg-neut-50 rounded-lg border border-neut-200">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-neut-600">
                      {t('edit_product.labels.product_code')}
                    </span>
                    <span className="ml-2 font-mono font-medium text-neut-prim">
                      {
                        sellerProductInventory?.platformProductsLength
                          ?.platformProduct?.materialCode
                      }
                    </span>
                  </div>
                  <div>
                    <span className="text-neut-600">
                      {t('edit_product.labels.name')}
                    </span>
                    <span className="ml-2 font-medium text-neut-prim">
                      {
                        sellerProductInventory?.platformProductsLength
                          ?.platformProduct?.name
                      }
                    </span>
                  </div>

                  <div>
                    <span className="text-neut-600">
                      {t('edit_product.labels.species')}
                    </span>
                    <span className="ml-2 font-medium text-neut-prim">
                      {getPropertyStringSafe(
                        sellerProductInventory?.platformProductsLength
                          ?.platformProduct?.properties,
                        'species'
                      )}
                    </span>
                  </div>
                  <div>
                    <span className="text-neut-600">
                      {t('edit_product.labels.grade')}
                    </span>
                    <span className="ml-2 font-medium text-neut-prim">
                      {getPropertyStringSafe(
                        sellerProductInventory?.platformProductsLength
                          ?.platformProduct?.properties,
                        'grade'
                      )}
                    </span>
                  </div>
                  <div>
                    <span className="text-neut-600">
                      {t('edit_product.labels.length')}
                    </span>
                    <span className="ml-2 font-medium text-neut-prim">
                      <span className="italic text-neut-500">
                        {
                          sellerProductInventory?.platformProductsLength
                            ?.lengthDisplayValue
                        }
                      </span>
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg text-neut-prim font-semibold">
                  {t('edit_product.sections.pricing_inventory')}
                </h2>
                <div>
                  <Button
                    variant="secondary"
                    onClick={() => setShowSettingsModal(true)}
                  >
                    <Settings size={16} />
                    {t('edit_product.buttons.stock_monitoring_settings')}
                  </Button>
                </div>
              </div>

              <div className="mb-4 p-4 bg-neut-50 rounded-lg border border-neut-200">
                <span className="block text-sm font-medium text-neut-prim mb-2">
                  {t('edit_product.scope.label')}
                </span>
                <RadioGroup
                  name="configurationScope"
                  value={selectedScope}
                  onChange={handleScopeChange}
                  options={[
                    {
                      value: SellerProductConfigurationScope.Facility,
                      label: t('edit_product.scope.facility'),
                      testId: 'scope-toggle-facility',
                    },
                    {
                      value: SellerProductConfigurationScope.Organization,
                      label: t('edit_product.scope.organization'),
                      testId: 'scope-toggle-organization',
                    },
                  ]}
                />
                <p className="text-xs text-neut-sec mt-2">
                  {t('edit_product.scope.helper')}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Form
                  formData={formData}
                  formFields={getPricingFormFields(
                    t,
                    (units || []).filter((unit) =>
                      isUnitAllowedForCategory(productCategory, unit.code)
                    )
                  )}
                  onSubmit={() => {}}
                  includeSubmitButton={false}
                />
              </div>

              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                <Form
                  formData={formData}
                  formFields={getInventoryFormFields(t)}
                  onSubmit={() => {}}
                  includeSubmitButton={false}
                />
              </div>
            </div>

            <div>
              <h2 className="text-lg text-neut-prim font-semibold mb-4">
                {t('edit_product.sections.additional_settings')}
              </h2>
              <div className="space-y-4">
                <Form
                  formData={formData}
                  formFields={getAdditionalSettingsFormFields(t)}
                  onSubmit={() => {}}
                  includeSubmitButton={false}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

const getPricingFormFields = (t: TFunction, units: Unit[]): FormField[] => [
  {
    name: 'unit',
    required: true,
    label: t('edit_product.labels.unit'),
    type: 'select',
    options: units.map((u) => ({ label: u.code, value: u.code })),
    placeholder: 'MBF',
  },
  {
    name: 'stock',
    label: t('edit_product.labels.stock_quantity'),
    type: 'number',
    placeholder: '0',
    required: true,
  },
];

const getInventoryFormFields = (t: TFunction): FormField[] => [
  {
    name: 'minimumOrderQuantity',
    label: t('edit_product.labels.minimum_order_quantity'),
    type: 'number',
    placeholder: '1',
    required: true,
  },
  {
    name: 'readyToShipDays',
    label: t('edit_product.labels.ready_to_ship_days'),
    type: 'number',
    placeholder: '0',
    required: true,
  },
  {
    name: 'replenishmentRatePerWeek',
    label: t('edit_product.labels.replenishment_rate'),
    type: 'number',
    placeholder: '0',
    required: true,
  },
];

const getAdditionalSettingsFormFields = (t: TFunction): FormField[] => [
  {
    name: 'internalNotes',
    label: t('edit_product.labels.internal_notes'),
    type: 'textarea',
    placeholder: t('edit_product.placeholders.internal_notes'),
    fullWidth: true,
  },
  {
    name: 'status',
    label: t('edit_product.labels.product_status'),
    type: 'select',
    placeholder: 'Active',
    options: [
      {
        label: t('edit_product.options.status.visible_to_all'),
        value: 'VISIBLE_TO_BUYERS',
      },
      {
        label: t('edit_product.options.status.visible_to_my_buyers'),
        value: 'VISIBLE_TO_MY_BUYERS',
      },
      { label: t('edit_product.options.status.inactive'), value: 'INACTIVE' },
    ],
    fullWidth: true,
  },
];

const getInitialFormData = (
  inventory: EditorSellerProductInventory | undefined | null,
  resolvedUnitCode?: string
) => ({
  unit: inventory ? (resolvedUnitCode ?? '') : '',
  stock: inventory?.quantity?.value.toString() ?? '',
  minimumOrderQuantity:
    inventory?.sellerProductConfiguration?.minimumOrderQuantity.value.toString() ??
    '',
  readyToShipDays:
    inventory?.sellerProductConfiguration?.readyToShipDays.toString() ?? '',
  replenishmentRatePerWeek:
    inventory?.sellerProductConfiguration?.replenishmentRatePerWeek?.value.toString() ??
    '',
  internalNotes: inventory?.sellerProductConfiguration?.internalNotes ?? '',
  status: inventory?.sellerProductConfiguration?.status ?? '',
});

const buildConfigurationInput = (
  data: Record<string, unknown>,
  readyToShipDays: number,
  sellerProductInventory: EditorSellerProductInventory,
  selectedScope: SellerProductConfigurationScope,
  loadedScope: SellerProductConfigurationScope
): UpsertSellerInventoryAndProductConfigurationMutationVariables['sellerProductConfigurationInput'] => {
  const input: UpsertSellerInventoryAndProductConfigurationMutationVariables['sellerProductConfigurationInput'] =
    {
      unit: data.unit as UnitCode,
      minimumOrderQuantity: {
        value: parseFloat(data.minimumOrderQuantity as string),
        unit: data.unit as UnitCode,
      },
      readyToShipDays,
      replenishmentRatePerWeek: {
        value: parseInt(data.replenishmentRatePerWeek as string),
        unit: data.unit as UnitCode,
      },
      internalNotes: data.internalNotes as string,
      status: data.status as SellerProductStatus,
    };

  // Changing scope can create a brand-new configuration (e.g. the first
  // organization-wide one), which would otherwise default to 0/0 stock levels
  // and fail the backend's "critical must be below low" validation. Carry the
  // current thresholds forward in pieces only on a scope change; omitting
  // them on ordinary same-scope edits avoids unit round-trip drift.
  const configuration = sellerProductInventory.sellerProductConfiguration;
  if (selectedScope !== loadedScope && configuration) {
    input.criticalStockLevel = {
      value: configuration.criticalStockLevelInPieces.value,
      unit: configuration.criticalStockLevelInPieces.unit,
    };
    input.lowStockLevel = {
      value: configuration.lowStockLevelInPieces.value,
      unit: configuration.lowStockLevelInPieces.unit,
    };
  }

  return input;
};

const buildMutationPayload = (
  data: Record<string, unknown>,
  sellerProductInventory: EditorSellerProductInventory,
  selectedScope: SellerProductConfigurationScope,
  configurationInput: UpsertSellerInventoryAndProductConfigurationMutationVariables['sellerProductConfigurationInput']
): UpsertSellerInventoryAndProductConfigurationMutationVariables => {
  // Scope-aware args come from the toggle's selected scope. For FACILITY we
  // use the row's own facility (correct even when the current config is
  // org-scoped and the user is switching to facility). The configuration is
  // scoped by facility, never by location.
  const scopeArgs =
    selectedScope === SellerProductConfigurationScope.Organization
      ? { scope: SellerProductConfigurationScope.Organization }
      : {
          scope: SellerProductConfigurationScope.Facility,
          facilityId: sellerProductInventory.location.facility.id,
        };

  return {
    platformProductsLengthId: sellerProductInventory.platformProductsLength.id,
    locationId: sellerProductInventory.location.id,
    ...scopeArgs,
    quantity: {
      value: parseFloat(data.stock as string),
      unit: data.unit as UnitCode,
    },
    sellerProductConfigurationInput: configurationInput,
  };
};
